/**
 * Offline production ticket printing via QZ Tray.
 *
 * Runs entirely against the local SQLite cache and the local QZ Tray
 * websocket — no Supabase calls. Used by `useOrders.sendItemsToOrder`
 * when the app is offline so kitchen tickets are still produced.
 *
 * All errors are caught and logged; this function never throws and never
 * blocks the order flow.
 */

import { ensureQZConnected, formatOrderReceipt, printReceipt } from "@/lib/qz-tray";
import type { LocalProduct, LocalProductionLocation } from "@/hooks/useSQLiteLocal";

export interface OfflinePrintItem {
  id: string;
  product_id: string | null;
  combo_id?: string | null;
  product_name: string;
  quantity: number;
  notes?: string | null;
}

export interface OfflinePrintOrderInfo {
  type: string;
  tableNumber?: string;
  tabNumber?: string;
  customerName?: string;
  waiterName?: string;
  notes?: string;
}

export interface OfflinePrintArgs {
  items: OfflinePrintItem[];
  orderInfo: OfflinePrintOrderInfo;
  /** When null, a short timestamp-derived placeholder is used. */
  orderNumber: number | null;
  restaurantId: string;
  getLocalProduct: (id: string) => LocalProduct | null;
  getLocalProductionLocation: (id: string) => LocalProductionLocation | null;
}

interface PrinterGroup {
  locationName: string;
  printerName: string;
  items: OfflinePrintItem[];
  itemIds: string[];
}

/**
 * Prints production tickets locally via QZ Tray.
 * Returns the ids of items that were successfully dispatched to a printer.
 */
export async function printProductionTicketOffline(
  args: OfflinePrintArgs
): Promise<string[]> {
  const { items, orderInfo, orderNumber, getLocalProduct, getLocalProductionLocation } = args;

  if (!items || items.length === 0) return [];

  // Resolve production_location for each item via local SQLite.
  const groups = new Map<string, PrinterGroup>();

  for (const item of items) {
    let locationId: string | null = null;

    // Combos are not in the local SQLite combos schema for production routing
    // in offline mode; fall back to the linked product when present.
    if (item.product_id) {
      const product = getLocalProduct(item.product_id);
      locationId = product?.production_location_id ?? null;
    }

    if (!locationId) {
      console.warn(
        `[OfflinePrint] Item "${item.product_name}" has no production location, skipping`
      );
      continue;
    }

    const location = getLocalProductionLocation(locationId);
    if (!location || !location.printer_name) {
      console.warn(
        `[OfflinePrint] Production location ${locationId} missing printer_name, skipping item "${item.product_name}"`
      );
      continue;
    }

    const key = location.id;
    const group = groups.get(key);
    if (group) {
      group.items.push(item);
      group.itemIds.push(item.id);
    } else {
      groups.set(key, {
        locationName: location.name,
        printerName: location.printer_name,
        items: [item],
        itemIds: [item.id],
      });
    }
  }

  if (groups.size === 0) {
    console.log("[OfflinePrint] No items routable to any printer");
    return [];
  }

  // Try to connect to QZ Tray. If unavailable, skip silently — the
  // reconnect-time sync path will reissue the print jobs.
  let connected = false;
  try {
    connected = await ensureQZConnected();
  } catch (e) {
    console.error("[OfflinePrint] ensureQZConnected threw:", e);
  }

  if (!connected) {
    console.log("[OfflinePrint] QZ Tray not connected, skipping local print");
    return [];
  }

  // Use a numeric placeholder for the ticket number when offline (no
  // server-assigned order_number yet). Last 5 digits of epoch keeps it
  // short and unique-enough for kitchen reference.
  const ticketNumber = orderNumber ?? Number(String(Date.now()).slice(-5));

  const printed: string[] = [];

  for (const group of groups.values()) {
    try {
      const content = formatOrderReceipt(
        ticketNumber,
        group.items.map((it) => ({
          name: it.product_name,
          quantity: it.quantity,
          notes: it.notes || undefined,
        })),
        group.locationName,
        orderInfo.type,
        orderInfo.type === "table" ? orderInfo.tableNumber : orderInfo.tabNumber,
        orderInfo.customerName,
        orderInfo.waiterName,
        orderInfo.notes,
        48 // default chars_per_line — getPrinterSettings would require network
      );

      const ok = await printReceipt(group.printerName, content, {
        logType: "pedidos",
        logIdentifier: ticketNumber,
        locationName: group.locationName,
      });

      if (ok) {
        printed.push(...group.itemIds);
        console.log(
          `[OfflinePrint] Sent ${group.items.length} item(s) to ${group.printerName} (${group.locationName})`
        );
      } else {
        console.warn(
          `[OfflinePrint] printReceipt returned false for ${group.printerName}`
        );
      }
    } catch (e) {
      console.error(
        `[OfflinePrint] Failed to print group ${group.locationName}:`,
        e
      );
    }
  }

  return printed;
}